import { Injectable, computed, inject, signal } from '@angular/core';
import {
  HttpClient,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  firstValueFrom,
  map,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AUTH_CONFIG } from './auth-config';
import {
  type AuthSession,
  type PasswordAuthResponse,
  type SessionMeta,
  type TokenRequest,
  type TokenResponse,
  type TotpSetupResponse,
} from './models';
import { expandEncodedScopes, tenantsWithPermission } from './scope-codec';

/** Proactive refresh lead time; aligns with server Jwt ClockSkewSeconds (30s). */
const EXPIRY_SKEW_MS = 30_000;

/** Marks a request that already retried after a silent refresh. */
export const AUTH_REFRESH_RETRIED = new HttpContextToken(() => false);

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly config = inject(AUTH_CONFIG);

  private readonly sessionSignal = signal<AuthSession | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight: Observable<TokenResponse> | null = null;
  private initialized = false;
  private visibilityListenerAttached = false;
  private pendingChallengeToken: string | null = null;
  private pendingChallengeEmail: string | null = null;
  private readonly onVisibilityOrFocus = (): void => {
    this.refreshIfStaleOnResume();
  };

  readonly session = this.sessionSignal.asReadonly();
  readonly isAuthenticated = computed(() => {
    const session = this.sessionSignal();
    return !!session?.accessToken && !isExpired(session.expiresAt);
  });
  readonly permissions = computed(() => new Set(this.sessionSignal()?.permissions ?? []));
  readonly tenantId = computed(() => this.sessionSignal()?.tenantId ?? null);
  /** Server-resolved tenant location code from the token response, when assigned. */
  readonly locationCode = computed(() => this.sessionSignal()?.locationCode ?? null);
  readonly email = computed(() => this.sessionSignal()?.email ?? null);
  readonly totpEnrolled = computed(() => this.sessionSignal()?.totpEnrolled ?? false);
  readonly mfaRequired = computed(() => this.sessionSignal()?.mfaRequired ?? false);
  readonly needsTotpEnrollment = computed(
    () => this.mfaRequired() && !this.totpEnrolled(),
  );

  /**
   * Restores session via refresh cookie when metadata exists (including after a
   * fresh browser launch). Call from APP_INITIALIZER before routing.
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.attachVisibilityListeners();

    const meta = this.readStoredMeta();
    if (!meta) {
      return;
    }

    try {
      await firstValueFrom(this.refreshSession(meta));
    } catch {
      this.clearLocalSession();
    }
  }

  login(
    tenantId: string,
    email: string,
    password: string,
  ): Observable<'authenticated' | 'totp-required'> {
    const body: TokenRequest = {
      tenantId,
      email,
      password,
      application: this.config.application,
    };

    return this.http
      .post<PasswordAuthResponse>(this.authUrl('/platform/auth/token'), body, {
        withCredentials: true,
      })
      .pipe(
        map((response) => {
          if ('requiresTotp' in response && response.requiresTotp) {
            this.pendingChallengeToken = response.challengeToken;
            this.pendingChallengeEmail = email;
            return 'totp-required' as const;
          }

          this.applyTokenResponse(response as TokenResponse, email);
          return 'authenticated' as const;
        }),
      );
  }

  verifyTotp(code: string): Observable<TokenResponse> {
    const challengeToken = this.pendingChallengeToken;
    const email = this.pendingChallengeEmail;
    if (!challengeToken || !email) {
      return throwError(() => new Error('No TOTP challenge is pending.'));
    }

    return this.http
      .post<TokenResponse>(
        this.authUrl('/platform/auth/totp/verify'),
        { challengeToken, code },
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          this.pendingChallengeToken = null;
          this.pendingChallengeEmail = null;
          this.applyTokenResponse(response, email);
        }),
      );
  }

  clearPendingChallenge(): void {
    this.pendingChallengeToken = null;
    this.pendingChallengeEmail = null;
  }

  beginTotpSetup(currentCode?: string): Observable<TotpSetupResponse> {
    return this.http.post<TotpSetupResponse>(this.authUrl('/platform/auth/totp/setup'), {
      currentCode: currentCode ?? null,
    });
  }

  confirmTotpSetup(code: string): Observable<void> {
    return this.http.post<void>(this.authUrl('/platform/auth/totp/confirm'), { code }).pipe(
      switchMap(() => this.refreshSession()),
      map(() => undefined),
      catchError((err) => {
        if (isAuthRejection(err)) {
          this.logout();
        }
        return throwError(() => err);
      }),
    );
  }

  disableTotp(code: string): Observable<void> {
    return this.http
      .request<void>('DELETE', this.authUrl('/platform/auth/totp'), { body: { code } })
      .pipe(
        switchMap(() => this.refreshSession()),
        map(() => undefined),
        catchError((err) => {
          if (isAuthRejection(err)) {
            this.logout();
          }
          return throwError(() => err);
        }),
      );
  }

  /**
   * Rotates the HttpOnly refresh cookie and returns a new access JWT.
   * Single-flight: concurrent callers share one HTTP refresh.
   */
  refreshSession(meta?: SessionMeta | null): Observable<TokenResponse> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const sessionMeta = meta ?? this.currentMeta();
    if (!sessionMeta?.tenantId) {
      return throwError(() => new Error('No session to refresh.'));
    }

    this.refreshInFlight = this.http
      .post<TokenResponse>(
        this.authUrl('/platform/auth/refresh'),
        {
          tenantId: sessionMeta.tenantId,
          application: sessionMeta.application || this.config.application,
        },
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          this.applyTokenResponse(response, sessionMeta.email);
        }),
        catchError((err) => {
          // Only drop the session when the refresh cookie is rejected. Transient
          // Platform/SQL 5xx must keep meta so the next navigation can retry.
          if (isAuthRejection(err)) {
            this.clearLocalSession();
          }
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInFlight = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.refreshInFlight;
  }

  /** Used by the HTTP interceptor to refresh once, then retry the failed request. */
  refreshAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> {
    if (req.context.get(AUTH_REFRESH_RETRIED)) {
      return throwError(() => new Error('Unauthorized after refresh.'));
    }

    return this.refreshSession().pipe(
      switchMap(() => {
        const token = this.accessToken();
        const retry = req.clone({
          context: req.context.set(AUTH_REFRESH_RETRIED, true),
          setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return next(retry);
      }),
      catchError((err) => {
        // Logout only when refresh cleared the session — not when the retried call fails.
        if (!this.hasRefreshableSession()) {
          this.logout();
        }
        return throwError(() => err);
      }),
    );
  }

  logout(navigateToLogin = true): void {
    this.clearSessionExpiry();
    this.clearPendingChallenge();
    const meta = this.sessionSignal() ?? this.readStoredMeta();
    const tenantId = meta?.tenantId ?? null;
    const application = meta?.application || this.config.application;
    const hadSession = this.sessionSignal() !== null || tenantId !== null;

    if (tenantId) {
      this.http
        .post(
          this.authUrl('/platform/auth/logout'),
          { tenantId, application },
          { withCredentials: true },
        )
        .subscribe({ error: () => undefined });
    }

    this.clearLocalSession();

    if (
      navigateToLogin &&
      (hadSession || !this.router.url.startsWith(this.config.loginUrl))
    ) {
      void this.router.navigateByUrl(this.config.loginUrl);
    }
  }

  /**
   * Clears an expired in-memory session.
   * @returns true when a non-expired access token remains.
   */
  ensureValidSession(): boolean {
    const session = this.sessionSignal();
    if (!session?.accessToken) {
      return false;
    }
    if (isExpired(session.expiresAt)) {
      return false;
    }
    return true;
  }

  can(permission: string): boolean {
    return this.permissions().has(permission);
  }

  canAny(permissions: readonly string[]): boolean {
    return permissions.some((permission) => this.can(permission));
  }

  canAll(permissions: readonly string[]): boolean {
    return permissions.every((permission) => this.can(permission));
  }

  /** Hyphenated tenant ids from JWT scopes that include the given permission. */
  tenantsWith(permission: string): string[] {
    const scopes = this.sessionSignal()?.scopes ?? [];
    return tenantsWithPermission(scopes, permission);
  }

  accessToken(): string | null {
    if (!this.ensureValidSession()) {
      return null;
    }
    return this.sessionSignal()!.accessToken;
  }

  hasRefreshableSession(): boolean {
    return this.currentMeta() !== null;
  }

  private applyTokenResponse(response: TokenResponse, email: string): void {
    const session: AuthSession = {
      accessToken: response.accessToken,
      accountId: response.accountId,
      tenantId: response.tenantId,
      application: response.application,
      authorizationVersion: response.authorizationVersion,
      scopes: [...response.scopes],
      permissions: [...expandEncodedScopes(response.scopes)],
      expiresAt: response.expiresAt,
      email,
      totpEnrolled: !!response.totpEnrolled,
      mfaRequired: !!response.mfaRequired,
      locationCode: normalizeLocationCode(response.locationCode),
    };
    this.persist(session);
  }

  private persist(session: AuthSession): void {
    this.sessionSignal.set(session);
    const meta: SessionMeta = {
      accountId: session.accountId,
      tenantId: session.tenantId,
      application: session.application,
      authorizationVersion: session.authorizationVersion,
      scopes: session.scopes,
      permissions: session.permissions,
      expiresAt: session.expiresAt,
      email: session.email,
      totpEnrolled: session.totpEnrolled,
      mfaRequired: session.mfaRequired,
      locationCode: session.locationCode ?? null,
    };
    // localStorage so refresh can restore after a fresh browser launch; JWT stays in memory.
    localStorage.setItem(this.config.sessionMetaKey, JSON.stringify(meta));
    if (this.config.legacySessionKey) {
      localStorage.removeItem(this.config.legacySessionKey);
    }
    this.scheduleProactiveRefresh(session.expiresAt);
  }

  private clearLocalSession(): void {
    this.clearSessionExpiry();
    this.sessionSignal.set(null);
    localStorage.removeItem(this.config.sessionMetaKey);
    if (this.config.legacySessionKey) {
      localStorage.removeItem(this.config.legacySessionKey);
    }
  }

  private currentMeta(): SessionMeta | null {
    const session = this.sessionSignal();
    if (session) {
      return {
        accountId: session.accountId,
        tenantId: session.tenantId,
        application: session.application,
        authorizationVersion: session.authorizationVersion,
        scopes: session.scopes,
        permissions: session.permissions,
        expiresAt: session.expiresAt,
        email: session.email,
        totpEnrolled: session.totpEnrolled,
        mfaRequired: session.mfaRequired,
        locationCode: session.locationCode ?? null,
      };
    }
    return this.readStoredMeta();
  }

  private readStoredMeta(): SessionMeta | null {
    try {
      const raw = localStorage.getItem(this.config.sessionMetaKey);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as SessionMeta;
      if (!parsed?.tenantId || !parsed.email) {
        localStorage.removeItem(this.config.sessionMetaKey);
        return null;
      }

      if (!parsed.permissions?.length && parsed.scopes?.length) {
        parsed.permissions = [...expandEncodedScopes(parsed.scopes)];
      }

      parsed.totpEnrolled = !!parsed.totpEnrolled;
      parsed.mfaRequired = !!parsed.mfaRequired;
      parsed.locationCode = normalizeLocationCode(parsed.locationCode) ?? null;

      return parsed;
    } catch {
      localStorage.removeItem(this.config.sessionMetaKey);
      return null;
    }
  }

  private scheduleProactiveRefresh(expiresAt: string): void {
    this.clearSessionExpiry();
    this.attachVisibilityListeners();

    const expires = Date.parse(expiresAt);
    if (!Number.isFinite(expires)) {
      this.logout();
      return;
    }

    const delayMs = Math.max(0, expires - EXPIRY_SKEW_MS - Date.now());
    this.refreshTimer = setTimeout(() => {
      this.refreshSession().subscribe({
        error: () => this.logout(),
      });
    }, delayMs);
  }

  private clearSessionExpiry(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private attachVisibilityListeners(): void {
    if (this.visibilityListenerAttached || typeof document === 'undefined') {
      return;
    }
    this.visibilityListenerAttached = true;
    document.addEventListener('visibilitychange', this.onVisibilityOrFocus);
    window.addEventListener('focus', this.onVisibilityOrFocus);
  }

  /**
   * Background tabs throttle timers; refresh when the tab becomes active again
   * if the access JWT is near or past expiry.
   */
  private refreshIfStaleOnResume(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    if (!this.hasRefreshableSession()) {
      return;
    }
    const session = this.sessionSignal();
    if (session?.accessToken && !isExpired(session.expiresAt)) {
      return;
    }
    this.refreshSession().subscribe({ error: () => undefined });
  }

  /** Prefixes Platform auth paths with {@link ResolvedAuthConfig.apiBaseUrl} when set. */
  private authUrl(path: string): string {
    const base = this.config.apiBaseUrl;
    return base ? `${base}${path}` : path;
  }
}

function isExpired(expiresAt: string): boolean {
  const expires = Date.parse(expiresAt);
  if (!Number.isFinite(expires)) {
    return true;
  }
  return Date.now() >= expires - EXPIRY_SKEW_MS;
}

function isAuthRejection(err: unknown): boolean {
  return err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403);
}

/** 3-character base-36 location code (e.g. 001), or null when not a code. */
function normalizeLocationCode(value: string | null | undefined): string | null {
  const trimmed = value?.trim().toUpperCase() ?? '';
  if (!/^[0-9A-Z]{3}$/.test(trimmed)) {
    return null;
  }
  return trimmed;
}
